<template>
  <div class="match-day-timeline">
    <div class="timeline-header">
      <h3>Match Day Nutrition</h3>
      <div v-if="activity" class="match-info">
        <span class="opposition">vs {{ activity.opposition }}</span>
        <span class="kick-off">KO: {{ formatTime(activity.time) }}</span>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
    </div>

    <div v-else class="timeline-container">
      <div class="timeline-track">
        <div
          class="current-time-marker"
          :style="{ left: currentTimePosition + '%' }"
        >
          <span class="current-label">NOW</span>
        </div>

        <div
          v-for="window in timelineWindows"
          :key="window.id"
          class="timeline-window"
          :class="[window.status, window.type]"
          :style="{ left: window.start + '%', width: window.width + '%' }"
        >
          <div class="window-content">
            <span class="window-icon">{{ window.icon }}</span>
            <span class="window-label">{{ window.label }}</span>
          </div>
        </div>

        <div class="match-marker" :style="{ left: matchPosition + '%' }">
          <span class="match-icon">M</span>
        </div>
      </div>

      <div class="time-labels">
        <span
          v-for="hour in timeLabels"
          :key="hour.time"
          class="time-label"
          :style="{ left: hour.position + '%' }"
        >
          {{ hour.time }}
        </span>
      </div>
    </div>

    <div class="windows-detail">
      <div
        v-if="preMatchWindow"
        class="window-card"
        :class="preMatchWindow.status"
      >
        <div class="window-header">
          <span class="window-type-icon pre-match">PM</span>
          <div class="window-title">
            <h4>Pre-Match Meal</h4>
            <span class="window-timing">3-4 hours before</span>
          </div>
          <div class="window-status-badge" :class="preMatchWindow.status">
            {{ getStatusLabel(preMatchWindow.status) }}
          </div>
        </div>
        <p class="window-message">{{ preMatchWindow.message }}</p>
        <div v-if="preMatchWindow.time_remaining" class="countdown">
          <span class="countdown-value">{{ preMatchWindow.time_remaining }}</span>
          <span class="countdown-label">remaining</span>
        </div>
        <div v-if="preMatchWindow.starts_in" class="countdown upcoming">
          <span class="countdown-label">Opens in</span>
          <span class="countdown-value">{{ preMatchWindow.starts_in }}</span>
        </div>
      </div>

      <div
        v-if="postMatchWindow"
        class="window-card"
        :class="postMatchWindow.status"
      >
        <div class="window-header">
          <span class="window-type-icon post-match">MR</span>
          <div class="window-title">
            <h4>Recovery Window</h4>
            <span class="window-timing">Within 60 mins after</span>
          </div>
          <div class="window-status-badge" :class="postMatchWindow.status">
            {{ getStatusLabel(postMatchWindow.status) }}
          </div>
        </div>
        <p class="window-message">{{ postMatchWindow.message }}</p>
        <div v-if="postMatchWindow.time_remaining" class="countdown">
          <span class="countdown-value">{{ postMatchWindow.time_remaining }}</span>
          <span class="countdown-label">remaining</span>
        </div>
      </div>

      <div v-if="!preMatchWindow && !postMatchWindow" class="no-windows">
        <p>Match timeline will appear once kick-off time is set</p>
      </div>
    </div>

    <div v-if="recommendations.length" class="recommendations-section">
      <h4>Recommended Meals</h4>
      <div class="recommendation-cards">
        <div
          v-for="meal in recommendations"
          :key="meal.id"
          class="recommendation-card"
          @click="selectMeal(meal)"
        >
          <div class="meal-name">{{ meal.name }}</div>
          <div class="meal-macros">
            <span class="macro">C: {{ meal.carbs }}g</span>
            <span class="macro">P: {{ meal.protein }}g</span>
          </div>
          <div class="meal-time">{{ meal.prep_time_mins }} min</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Nutrition from "@/services/nutrition-service";

export default {
  name: "MatchDayTimeline",

  props: {
    userId: {
      type: [Number, String],
      required: true,
    },
    teamId: {
      type: [Number, String],
      default: null,
    },
    date: {
      type: String,
      default: () => new Date().toISOString().split("T")[0],
    },
  },

  data() {
    return {
      loading: false,
      activity: null,
      preMatchWindow: null,
      postMatchWindow: null,
      recommendations: [],
      currentTime: new Date(),
      refreshInterval: null,
    };
  },

  computed: {
    matchPosition() {
      if (!this.activity || !this.activity.time) return 50;
      const matchHour = parseInt(this.activity.time.split(":")[0], 10);
      const matchMin = parseInt(this.activity.time.split(":")[1], 10);
      return this.timeToPosition(matchHour, matchMin);
    },

    currentTimePosition() {
      const hours = this.currentTime.getHours();
      const mins = this.currentTime.getMinutes();
      return this.timeToPosition(hours, mins);
    },

    timelineWindows() {
      if (!this.activity || !this.activity.time) return [];

      const matchHour = parseInt(this.activity.time.split(":")[0], 10);
      const matchMin = parseInt(this.activity.time.split(":")[1], 10);

      const windows = [];

      // Pre-match meal window (3-4 hrs before)
      const preStart = this.timeToPosition(matchHour - 4, matchMin);
      const preEnd = this.timeToPosition(matchHour - 3, matchMin);
      windows.push({
        id: "pre-match",
        type: "pre-match",
        icon: "PM",
        label: "Pre-Match Meal",
        start: preStart,
        width: preEnd - preStart,
        status: this.preMatchWindow?.status || "upcoming",
      });

      // Pre-match snack (1-2 hrs before)
      const snackStart = this.timeToPosition(matchHour - 2, matchMin);
      const snackEnd = this.timeToPosition(matchHour - 1, matchMin);
      windows.push({
        id: "pre-snack",
        type: "snack",
        icon: "S",
        label: "Light Snack",
        start: snackStart,
        width: snackEnd - snackStart,
        status: "normal",
      });

      // Post-match recovery (60 mins after)
      const matchEndHour = matchHour + 2; // ~105 mins for match
      const postStart = this.timeToPosition(matchEndHour, matchMin);
      const postEnd = this.timeToPosition(matchEndHour + 1, matchMin);
      windows.push({
        id: "post-match",
        type: "post-match",
        icon: "MR",
        label: "Recovery",
        start: postStart,
        width: postEnd - postStart,
        status: this.postMatchWindow?.status || "upcoming",
      });

      return windows;
    },

    timeLabels() {
      const labels = [];
      for (let h = 8; h <= 22; h += 2) {
        labels.push({
          time: `${h}:00`,
          position: this.timeToPosition(h, 0),
        });
      }
      return labels;
    },
  },

  async mounted() {
    await this.loadRecommendations();
    this.startTimeRefresh();
  },

  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },

  methods: {
    async loadRecommendations() {
      this.loading = true;
      try {
        const response = await Nutrition.getSmartRecommendations(
          this.userId,
          this.teamId,
          this.date
        );

        if (response.data && response.data.success) {
          const data = response.data.data;
          this.activity = data.activity;
          this.preMatchWindow = data.pre_match_window;
          this.postMatchWindow = data.post_match_window;
          this.recommendations = data.recommendations || [];
        }
      } catch (error) {
        console.error("Error loading recommendations:", error);
      } finally {
        this.loading = false;
      }
    },

    startTimeRefresh() {
      this.refreshInterval = setInterval(() => {
        this.currentTime = new Date();
      }, 60000); // Update every minute
    },

    timeToPosition(hours, mins) {
      // Timeline spans 8:00 to 22:00 (14 hours)
      const startHour = 8;
      const totalHours = 14;
      const totalMins = totalHours * 60;
      const currentMins = (hours - startHour) * 60 + mins;
      return Math.max(0, Math.min(100, (currentMins / totalMins) * 100));
    },

    formatTime(time) {
      if (!time) return "";
      const [hours, mins] = time.split(":");
      const h = parseInt(hours, 10);
      const period = h >= 12 ? "PM" : "AM";
      const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
      return `${displayHour}:${mins} ${period}`;
    },

    getStatusLabel(status) {
      const labels = {
        active: "NOW",
        upcoming: "SOON",
        missed: "PASSED",
        closed: "CLOSED",
      };
      return labels[status] || status;
    },

    selectMeal(meal) {
      this.$emit("select-meal", meal);
    },
  },

  watch: {
    date() {
      this.loadRecommendations();
    },
  },
};
</script>

<style lang="scss" scoped>
.match-day-timeline {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
}

.match-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.opposition {
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
}

.kick-off {
  font-size: 0.8rem;
  color: #dc3545;
  font-weight: 500;
}

.loading-state {
  text-align: center;
  padding: 30px;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #f0f0f0;
  border-top-color: #007bff;
  border-radius: 50%;
  margin: 0 auto;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.timeline-container {
  position: relative;
  margin-bottom: 24px;
}

.timeline-track {
  position: relative;
  height: 50px;
  background: #f0f0f0;
  border-radius: 8px;
  overflow: visible;
}

.timeline-window {
  position: absolute;
  top: 5px;
  height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  transition: all 0.3s ease;

  &.pre-match {
    background: rgba(220, 53, 69, 0.2);
    border: 2px solid #dc3545;
  }

  &.snack {
    background: rgba(253, 126, 20, 0.2);
    border: 2px solid #fd7e14;
  }

  &.post-match {
    background: rgba(0, 123, 255, 0.2);
    border: 2px solid #007bff;
  }

  &.active {
    animation: pulse 2s infinite;
  }
}

.window-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.window-icon {
  font-weight: 700;
  font-size: 0.75rem;
}

.window-label {
  font-size: 0.6rem;
  white-space: nowrap;
}

.current-time-marker {
  position: absolute;
  top: -15px;
  transform: translateX(-50%);
  z-index: 10;
}

.current-label {
  background: #28a745;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 700;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #28a745;
  }
}

.match-marker {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
}

.match-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.85rem;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
}

.time-labels {
  position: relative;
  height: 20px;
  margin-top: 8px;
}

.time-label {
  position: absolute;
  transform: translateX(-50%);
  font-size: 0.7rem;
  color: #888;
}

.windows-detail {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.window-card {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 14px;
  border-left: 4px solid #ccc;

  &.active {
    border-left-color: #28a745;
    background: #d4edda;
  }

  &.upcoming {
    border-left-color: #ffc107;
  }

  &.missed,
  &.closed {
    border-left-color: #6c757d;
    opacity: 0.7;
  }
}

.window-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.window-type-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  color: white;

  &.pre-match {
    background: linear-gradient(135deg, #dc3545, #c82333);
  }

  &.post-match {
    background: linear-gradient(135deg, #007bff, #0056b3);
  }
}

.window-title {
  flex: 1;

  h4 {
    font-size: 0.95rem;
    font-weight: 600;
    color: #333;
    margin: 0;
  }

  .window-timing {
    font-size: 0.75rem;
    color: #666;
  }
}

.window-status-badge {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 12px;

  &.active {
    background: #28a745;
    color: white;
  }

  &.upcoming {
    background: #ffc107;
    color: #333;
  }

  &.missed,
  &.closed {
    background: #6c757d;
    color: white;
  }
}

.window-message {
  font-size: 0.85rem;
  color: #555;
  margin: 0 0 10px 0;
}

.countdown {
  display: flex;
  align-items: baseline;
  gap: 6px;

  &.upcoming {
    flex-direction: row;
  }
}

.countdown-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #dc3545;
}

.countdown-label {
  font-size: 0.75rem;
  color: #666;
}

.no-windows {
  text-align: center;
  padding: 20px;
  color: #666;
  font-size: 0.9rem;
}

.recommendations-section {
  h4 {
    font-size: 0.9rem;
    font-weight: 600;
    color: #333;
    margin: 0 0 12px 0;
  }
}

.recommendation-cards {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 8px;
}

.recommendation-card {
  flex: 0 0 140px;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e9ecef;
    transform: translateY(-2px);
  }
}

.meal-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meal-macros {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}

.macro {
  font-size: 0.7rem;
  color: #666;
}

.meal-time {
  font-size: 0.7rem;
  color: #888;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>
